import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiService, showToast } from './api.service';

interface AuditRecord {
  id: string;
  title: string;
  assignee: string;
  date: string;
  status: string;
  score: string;
  audit_date?: string | null;
  notes?: string | null;
  findings?: string | null;
}

@Component({
  selector: 'app-admin-compliance-audits',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-6 md:p-8 min-h-full relative" style="background:#f1f9fe;">
      <div class="flex items-center justify-between mb-8 shrink-0">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 tracking-tight">Safety Audits</h1>
          <p class="text-slate-500">Review health & safety compliance records</p>
        </div>
        <button (click)="openCreateAudit()" 
                class="px-5 py-2.5 text-white rounded-2xl font-bold shadow-md transition-all hover:opacity-90 flex items-center gap-1.5 text-sm"
                style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
          <i class="ti ti-clipboard-list text-[18px]"></i> New Audit
        </button>
      </div>

      <div class="flex-1 overflow-auto">
        @if (loadError()) {
          <div class="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 flex items-center justify-between gap-3">
            <span>{{ loadError() }}</span>
            <button (click)="loadAudits()" class="px-3 py-1.5 rounded-lg bg-white border border-red-100 text-red-700 font-bold">Retry</button>
          </div>
        }
        @if (isLoading()) {
          <div class="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 text-center text-sm font-bold text-slate-500">
            Loading safety audits...
          </div>
        } @else if (audits.length === 0) {
          <div class="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 text-center">
            <i class="ti ti-clipboard-list text-3xl text-slate-300"></i>
            <p class="mt-3 text-sm font-bold text-slate-700">No safety audits yet.</p>
            <p class="text-xs text-slate-500">Create the first audit record to start tracking compliance.</p>
          </div>
        } @else {
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (audit of audits; track audit.id) {
            <button (click)="viewAudit(audit)" class="bg-white rounded-3xl shadow-sm p-5 hover:shadow-md transition-all duration-300 relative flex flex-col items-start text-left border hover:border-[#2B9AD2] group cursor-pointer w-full" style="border-color:#EEF2F7;">
              
              <!-- Floating Status Tag -->
              <div class="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-bold border shadow-sm"
                   [ngClass]="{
                     'bg-emerald-50 text-emerald-600 border-emerald-100': audit.status === 'Passed',
                     'bg-amber-50 text-amber-600 border-amber-100': audit.status === 'Pending',
                     'bg-red-50 text-red-600 border-red-100': audit.status === 'Action Required'
                   }">
                {{audit.status}}
              </div>

              <!-- Header with Icon -->
              <div class="flex items-center gap-3.5 mb-4 mt-1 w-full">
                <div class="w-12 h-12 rounded-2xl bg-[#F2F8FD] border border-slate-50 flex items-center justify-center text-[#2B9AD2] shrink-0 shadow-sm">
                  <i class="ti ti-checkbox text-xl"></i>
                </div>
                <div>
                   <h3 class="text-base font-black text-slate-900 leading-tight group-hover:text-[#2B9AD2] transition-colors">{{audit.title}}</h3>
                   <p class="text-[10px] font-bold text-slate-500 mt-0.5">By: {{audit.assignee}}</p>
                </div>
              </div>

              <!-- Information Details Box -->
              <div class="bg-[#F8FBFE] p-4 rounded-2xl border border-slate-50 mb-4 flex-1 flex flex-col justify-center w-full">
                 <div class="space-y-1.5 text-xs text-slate-600 font-medium">
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-1.5">
                      <i class="ti ti-calendar text-[14px] text-slate-400"></i> <span>Date</span>
                    </div>
                    <span class="font-bold text-slate-700">{{audit.date}}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-1.5">
                      <i class="ti ti-chart-bar text-[14px] text-slate-400"></i> <span>Score</span>
                    </div>
                    <span class="font-bold text-sm"
                          [class.text-emerald-600]="audit.score === '100%'"
                          [class.text-amber-600]="audit.score !== '100%' && audit.score !== '-'"
                          [class.text-slate-400]="audit.score === '-'">
                       {{audit.score}}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Footer Actions -->
              <div class="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto w-full">
                <span class="text-xs font-bold text-slate-400 flex items-center gap-1">
                  <i class="ti ti-id text-[14px] text-slate-400"></i> {{audit.id}}
                </span>
                <span class="px-3 py-1.5 bg-slate-50 border border-slate-100 text-slate-700 rounded-xl text-xs font-extrabold shadow-sm group-hover:bg-[#2B9AD2] group-hover:text-white group-hover:border-transparent transition-all flex items-center gap-1">
                   View Report <i class="ti ti-chevron-right text-sm"></i>
                </span>
              </div>

            </button>
          }
          </div>
        }
      </div>

      <!-- Create/Edit Audit Modal -->
      @if (showCreateModal()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div class="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
              <h2 class="text-xl font-bold text-slate-900">{{ editingAuditId() ? 'Edit Safety Audit' : 'New Safety Audit' }}</h2>
              <button (click)="showCreateModal.set(false)" class="text-slate-400 hover:text-slate-600">
                <i class="ti ti-x text-[18px]"></i>
              </button>
            </div>
            
            <form #auditForm="ngForm" (ngSubmit)="onSubmit(auditForm)" class="flex flex-col flex-1 overflow-hidden">
              <div class="p-6 overflow-y-auto flex-1 space-y-4">
                <div>
                  <label for="title" class="block text-sm font-medium text-slate-700 mb-1">Audit Title</label>
                  <input type="text" id="title" name="title" [(ngModel)]="formData.title" required class="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none">
                </div>
                <div>
                  <label for="assignee" class="block text-sm font-medium text-slate-700 mb-1">Assignee</label>
                  <input type="text" id="assignee" name="assignee" [(ngModel)]="formData.assignee" required class="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none">
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label for="date" class="block text-sm font-medium text-slate-700 mb-1">Date</label>
                    <input type="date" id="date" name="date" [(ngModel)]="formData.date" required class="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none">
                  </div>
                  <div>
                    <label for="status" class="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select id="status" name="status" [(ngModel)]="formData.status" required class="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none bg-white">
                      <option value="Pending">Pending</option>
                      <option value="Passed">Passed</option>
                      <option value="Action Required">Action Required</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label for="score" class="block text-sm font-medium text-slate-700 mb-1">Score (Optional)</label>
                  <input type="text" id="score" name="score" [(ngModel)]="formData.score" class="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none">
                </div>
                <div>
                  <label for="findings" class="block text-sm font-medium text-slate-700 mb-1">Key Findings</label>
                  <textarea id="findings" name="findings" [(ngModel)]="formData.findings" rows="4" class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#2B9AD2] focus:ring-1 focus:ring-[#2B9AD2] outline-none resize-none"></textarea>
                </div>
              </div>
              
              <div class="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0">
                <button type="button" (click)="showCreateModal.set(false)" class="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" [disabled]="!auditForm.valid || isSaving()" class="px-6 py-2 bg-[#2B9AD2] text-white font-bold rounded-lg shadow-sm hover:bg-[#1B78AE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {{ isSaving() ? 'Saving...' : (editingAuditId() ? 'Save Changes' : 'Create Audit') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      }

      <!-- View Audit Modal -->
      @if (selectedAudit()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div class="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
              <h2 class="text-xl font-bold text-slate-900">Audit Details</h2>
              <button (click)="selectedAudit.set(null)" class="text-slate-400 hover:text-slate-600">
                <i class="ti ti-x text-[18px]"></i>
              </button>
            </div>
            
            <div class="p-6 overflow-y-auto flex-1">
              <div class="flex justify-between items-start mb-6">
                <div>
                  <h3 class="text-2xl font-bold text-slate-900 mb-2">{{selectedAudit()?.title}}</h3>
                  <p class="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <i class="ti ti-user text-[18px]"></i> {{selectedAudit()?.assignee}}
                  </p>
                </div>
                <div class="text-right">
                  <span class="px-3 py-1.5 rounded-md text-sm font-bold uppercase tracking-wider border inline-block mb-2"
                        [ngClass]="{
                          'bg-emerald-50 text-emerald-600 border-emerald-200': selectedAudit()?.status === 'Passed',
                          'bg-amber-50 text-amber-600 border-amber-200': selectedAudit()?.status === 'Pending',
                          'bg-red-50 text-red-600 border-red-200': selectedAudit()?.status === 'Action Required'
                        }">
                    {{selectedAudit()?.status}}
                  </span>
                  <p class="text-xs font-bold text-slate-400">{{selectedAudit()?.id}}</p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div class="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Audit Information</p>
                  <div class="space-y-4">
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-medium text-slate-600">Date Completed</span>
                      <span class="text-sm font-bold text-slate-900">{{selectedAudit()?.date}}</span>
                    </div>
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-medium text-slate-600">Overall Score</span>
                      <span class="text-sm font-bold"
                            [ngClass]="{
                              'text-emerald-600': selectedAudit()?.score === '100%',
                              'text-amber-600': selectedAudit()?.score !== '100%' && selectedAudit()?.score !== '-',
                              'text-slate-400': selectedAudit()?.score === '-'
                            }">
                        {{selectedAudit()?.score}}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div class="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Key Findings</p>
                  <ul class="space-y-2 text-sm text-slate-700">
                     @if ((selectedAudit()?.findings || '').trim()) {
                      @for (finding of findingLines(selectedAudit()?.findings || ''); track finding) {
                        <li class="flex items-start gap-2">
                          <i class="ti ti-point-filled text-[18px] text-[#2B9AD2] shrink-0"></i>
                          <span>{{ finding }}</span>
                        </li>
                      }
                    } @else if (selectedAudit()?.status === 'Passed') {
                      <li class="flex items-start gap-2">
                        <i class="ti ti-circle-check text-[18px] text-emerald-500 shrink-0"></i>
                        <span>All safety protocols followed correctly.</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <i class="ti ti-circle-check text-[18px] text-emerald-500 shrink-0"></i>
                        <span>PPE worn at all times.</span>
                      </li>
                    } @else if (selectedAudit()?.status === 'Action Required') {
                      <li class="flex items-start gap-2">
                        <i class="ti ti-alert-circle text-[18px] text-red-500 shrink-0"></i>
                        <span>Missing documentation for hazard ID.</span>
                      </li>
                      <li class="flex items-start gap-2">
                        <i class="ti ti-alert-triangle text-[18px] text-amber-500 shrink-0"></i>
                        <span>Site cleanup required.</span>
                      </li>
                    } @else {
                      <li class="flex items-start gap-2">
                        <i class="ti ti-clock text-[18px] text-slate-400 shrink-0"></i>
                        <span>Audit is currently under review.</span>
                      </li>
                    }
                  </ul>
                </div>
              </div>
              
              <div class="flex gap-3 justify-end pt-6 border-t border-slate-100">
                <button (click)="downloadAudit(selectedAudit()!)" class="px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-100 transition-colors text-sm flex items-center gap-2">
                  <i class="ti ti-download text-[18px]"></i> Print / Save PDF
                </button>
                <button (click)="openEditAudit(selectedAudit()!)" class="px-4 py-2 text-white rounded-2xl font-semibold shadow-sm transition-opacity hover:opacity-90 transition-colors text-sm flex items-center gap-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                  <i class="ti ti-edit text-[18px]"></i> Edit Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminComplianceAuditsComponent implements OnInit {
  private api = inject(ApiService);

  audits: AuditRecord[] = [];
  showCreateModal = signal(false);
  selectedAudit = signal<AuditRecord | null>(null);
  editingAuditId = signal<string | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  loadError = signal('');
  formData = {
    title: '',
    assignee: '',
    date: '',
    status: 'Pending',
    score: '-',
    findings: ''
  };

  ngOnInit() {
    this.loadAudits();
  }

  loadAudits() {
    this.isLoading.set(true);
    this.loadError.set('');
    this.api.getAudits().subscribe({
      next: (res: any) => {
        this.audits = res?.data?.audits || [];
        this.isLoading.set(false);
      },
      error: () => {
        this.loadError.set('Unable to load safety audits.');
        this.isLoading.set(false);
      }
    });
  }

  viewAudit(audit: AuditRecord) {
    this.selectedAudit.set(audit);
  }

  openCreateAudit() {
    this.editingAuditId.set(null);
    this.formData = {
      title: '',
      assignee: '',
      date: this.toDateInput(new Date()),
      status: 'Pending',
      score: '-',
      findings: ''
    };
    this.showCreateModal.set(true);
  }

  openEditAudit(audit: AuditRecord) {
    this.editingAuditId.set(audit.id);
    this.formData = {
      title: audit.title,
      assignee: audit.assignee,
      date: this.parseDisplayDate(audit.date),
      status: audit.status,
      score: audit.score || '-',
      findings: audit.findings || ''
    };
    this.selectedAudit.set(null);
    this.showCreateModal.set(true);
  }

  onSubmit(form: NgForm) {
    if (!form.valid || this.isSaving()) return;

    const payload = {
      id: this.editingAuditId(),
      title: this.formData.title.trim(),
      assignee: this.formData.assignee.trim(),
      audit_date: this.formData.date,
      status: this.formData.status,
      score: this.formData.score?.trim() || '-',
      findings: this.formData.findings?.trim() || ''
    };

    this.isSaving.set(true);
    this.api.saveAudit(payload).subscribe({
      next: (res: any) => {
        const savedAudit: AuditRecord | undefined = res?.data?.audit;
        if (savedAudit) {
          if (this.editingAuditId()) {
            this.audits = this.audits.map(audit => audit.id === this.editingAuditId() ? savedAudit : audit);
            this.selectedAudit.set(savedAudit);
          } else {
            this.audits = [savedAudit, ...this.audits];
          }
        } else {
          this.loadAudits();
        }
        this.showCreateModal.set(false);
        this.editingAuditId.set(null);
        this.isSaving.set(false);
        form.resetForm();
        showToast('success', res?.message || 'Audit saved.');
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Unable to save audit.');
      }
    });
  }

  downloadAudit(audit: AuditRecord) {
    this.api.downloadAuditPdf(audit.id).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${audit.id}_Safety_Audit.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => showToast('error', err?.error?.message || 'Unable to download audit PDF.')
    });
  }

  private parseDisplayDate(displayDate: string): string {
    if (!displayDate) return this.toDateInput(new Date());
    const date = new Date(displayDate);
    return Number.isNaN(date.getTime()) ? this.toDateInput(new Date()) : this.toDateInput(date);
  }

  private toDateInput(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  findingLines(findings: string): string[] {
    return findings.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  }

}




